import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKato } from '../../../contexts/kato.context';
import { useShared } from '../../../contexts/shared.context';
import KatoSelects from '../components/kato-selects.component';
import FooterComponent from '../components/footer.component';
import Field from '../../../components/field/field.component';
import { toast } from 'react-toastify';
import {
  getIncreaseVal,
  getPopulationDetails,
  isEmpty,
  t,
} from '../../../helpers/utils';
import { GeneralInfoStatus } from '../../../types/kato.types';
import { updateGeneralInfoData } from '../../../requests/supervisor.request';
import classes from './styles.module.scss';
const baseClass = 'population-dynamic-tab';
const translationKey = 'admin.population-dynamic';

const populationFields = [
  'maleCount',
  'femaleCount',
  'populationCount',
  'birthCount',
  'deathCount',
  'naturalCount',
  'immigrationCount',
  'emigrationCount',
  'saldoMigration',
] as const;
const employmentFields = [
  'populationEmploymentCount',
  'economicallyActivePopulation',
  'youthPopulationCount',
  'employedPopulationCount',
  'hiredPopulationCount',
  'selfEmployedPopulationCount',
  'unemployedPopulationCount',
] as const;
const disabledFields = [
  'populationCount',
  'naturalCount',
  'saldoMigration',
  'economicallyActivePopulation',
];
const PopulationDynamicTab = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const { katoGeneralInfo, updatedGeneralInfo, currentLevel } = useKato();
  const { populationIncreaseDynamicStats, populationDetailsStats, status } =
    katoGeneralInfo || {};
  const { selectedInfoYear } = useShared();

  const [formData, setFormData] = useState({
    populationIncreaseDynamicStats: populationIncreaseDynamicStats || [],
    populationDetailsStats: populationDetailsStats || {},
    status: status || GeneralInfoStatus.IN_PROCESS,
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const handleSave = () => {
    if (validateFields()) {
      const data = {
        populationIncreaseDynamicStats: formData.populationIncreaseDynamicStats,
        populationDetailsStats: formData.populationDetailsStats,
        status: formData.status,
      };
      updateGeneralInfoData(katoGeneralInfo.kato, data, selectedInfoYear).then(
        () => {
          updatedGeneralInfo(selectedInfoYear);
          toast.success(t(`admin.success`, language));
        },
      );
    }
  };

  const validateFields = () => {
    const newErrors: Record<string, boolean> = {};

    const yearFields = formData.populationIncreaseDynamicStats;

    if (
      yearFields.some(
        (stat) => isEmpty(stat.value) && stat.year !== selectedInfoYear,
      )
    ) {
      yearFields.forEach((stat) => {
        if (isEmpty(stat.value) && stat.year !== selectedInfoYear) {
          newErrors['increase.' + stat.year] = true;
        }
      });
    }

    if (
      populationFields.some(
        (field) =>
          isEmpty(formData.populationDetailsStats[field]) &&
          !disabledFields.includes(field),
      ) ||
      employmentFields.some(
        (field) =>
          isEmpty(formData.populationDetailsStats[field]) &&
          !disabledFields.includes(field),
      )
    ) {
      populationFields.forEach((field) => {
        if (
          isEmpty(formData.populationDetailsStats[field]) &&
          !disabledFields.includes(field)
        ) {
          newErrors['stats.' + field] = true;
        }
      });
      employmentFields.forEach((field) => {
        if (
          isEmpty(formData.populationDetailsStats[field]) &&
          !disabledFields.includes(field)
        ) {
          newErrors['stats.' + field] = true;
        }
      });
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  const renderFields = (lang: 'ru' | 'kz') => {
    return (
      <div className={classes['fields-container']}>
        {formData.populationIncreaseDynamicStats && (
          <div className={classes['field-block']}>
            <div className={classes['field-block__title']}>
              {t(`${translationKey}.increase.title`, lang)}
            </div>
            <div className={classes['field-block__fields']}>
              {formData.populationIncreaseDynamicStats.map((item, index) => {
                const isDisabled = selectedInfoYear === item.year;
                const value = isDisabled
                  ? getPopulationDetails(
                      formData.populationDetailsStats,
                      'populationCount',
                    )
                  : (item.value ?? '');
                return (
                  <Field
                    key={item.year}
                    value={value}
                    onChange={(newValue) => {
                      const val = isEmpty(newValue) ? '' : +newValue;
                      const newStats =
                        formData.populationIncreaseDynamicStats.map((stat) =>
                          stat.year === item.year
                            ? { ...stat, value: val }
                            : stat,
                        );
                      setFormData({
                        ...formData,
                        populationIncreaseDynamicStats: newStats,
                      });
                    }}
                    type="number"
                    label={item.year.toString()}
                    disabled={isDisabled}
                    required
                    error={errors['increase.' + item.year]}
                  />
                );
              })}
            </div>
          </div>
        )}

        {formData.populationIncreaseDynamicStats && (
          <>
            <div className={classes['field-block']}>
              <div className={classes['field-block__title']}>
                {t('population', lang)}
              </div>
              <div className={classes['field-block__fields']}>
                {populationFields.map((field) => {
                  const isDisabled = disabledFields.includes(field);
                  const value = isDisabled
                    ? getPopulationDetails(
                        formData.populationDetailsStats,
                        field,
                      )
                    : (formData.populationDetailsStats[field] ?? '');
                  return (
                    <Field
                      key={field}
                      value={value}
                      onChange={(newValue) => {
                        const val = isEmpty(newValue) ? '' : +newValue;
                        const newStats = {
                          ...formData.populationDetailsStats,
                          [field]: val,
                        };
                        setFormData({
                          ...formData,
                          populationDetailsStats: newStats,
                        });
                      }}
                      type="number"
                      label={t(`${translationKey}.stats.${field}`, lang)}
                      disabled={disabledFields.includes(field)}
                      required
                      error={errors['stats.' + field]}
                    />
                  );
                })}
              </div>
            </div>
            <div className={classes['field-block']}>
              <div className={classes['field-block__title']}>
                {t('employment', lang)}
              </div>
              <div className={classes['field-block__fields']}>
                {employmentFields.map((field) => {
                  const isDisabled = disabledFields.includes(field);
                  const value = isDisabled
                    ? getPopulationDetails(
                        formData.populationDetailsStats,
                        field,
                      )
                    : (formData.populationDetailsStats[field] ?? '');
                  return (
                    <Field
                      key={field}
                      value={value}
                      onChange={(newValue) => {
                        const val = isEmpty(newValue) ? '' : +newValue;
                        const newStats = {
                          ...formData.populationDetailsStats,
                          [field]: val,
                        };
                        setFormData({
                          ...formData,
                          populationDetailsStats: newStats,
                        });
                      }}
                      type="number"
                      label={t(`${translationKey}.stats.${field}`, lang)}
                      disabled={disabledFields.includes(field)}
                      required
                      error={errors['stats.' + field]}
                    />
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    setFormData({
      populationIncreaseDynamicStats: (
        populationIncreaseDynamicStats || []
      ).sort((a, b) => a.year - b.year),
      populationDetailsStats: populationDetailsStats || {},
      status: status || GeneralInfoStatus.IN_PROCESS,
    });
  }, [
    populationIncreaseDynamicStats,
    populationDetailsStats,
    katoGeneralInfo.kato,
    selectedInfoYear,
  ]);

  if (
    !populationIncreaseDynamicStats ||
    !populationDetailsStats ||
    !katoGeneralInfo
  )
    return null;

  return (
    <div className={classes[baseClass]}>
      <div className={classes[`grid`]}>
        <KatoSelects lang={language as 'ru' | 'kz'} />
        <KatoSelects lang={language === 'ru' ? 'kz' : 'ru'} />
        {renderFields(language as 'ru' | 'kz')}
        {renderFields(language === 'ru' ? 'kz' : 'ru')}
      </div>

      <FooterComponent
        onSave={handleSave}
        status={formData.status}
        validateFields={validateFields}
      />
    </div>
  );
};

export default PopulationDynamicTab;
